import { useState, useRef, useEffect } from 'react';
import { X, Download, MoreHorizontal, Check } from 'lucide-react';
import { Button } from './ui/button';
import html2canvas from 'html2canvas';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Shareable Scorecard Component - Par Seeker Branded
export const ShareableScorecard = ({ 
  score, 
  user, 
  course,
  onClose, 
  onShareToFeed,
  onEdit,  // New prop for edit functionality
  isOpen 
}) => {
  const cardRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [currentHandicap, setCurrentHandicap] = useState(null);

  // Fetch the current handicap from the /api/handicap endpoint when modal opens
  // This endpoint returns the same "Official Handicap Index" shown on the dashboard
  useEffect(() => {
    if (isOpen) {
      const fetchHandicap = async () => {
        try {
          const response = await axios.get(`${API}/handicap`);
          // Use handicap_index which matches the dashboard's "Official Handicap Index"
          if (response.data?.handicap_index !== undefined && response.data?.handicap_index !== null) {
            setCurrentHandicap(response.data.handicap_index);
          } else {
            // Fallback to user prop if handicap_index is not available
            setCurrentHandicap(user?.handicap);
          }
        } catch (error) {
          console.error("Failed to fetch current handicap:", error);
          // Fallback to user prop
          setCurrentHandicap(user?.handicap);
        }
      };
      fetchHandicap();
    }
  }, [isOpen, user?.handicap]);

  if (!isOpen || !score) return null;

  // Determine if this is a 9-hole round
  const holesPlayed = score.holes_played || (score.hole_scores?.length || 18);
  const isNineHoleRound = holesPlayed === 9;

  // Get score and par values
  const totalScore = score.total_score || 0;
  const par = score.par || course?.par || (isNineHoleRound ? 36 : 72);
  
  // Get hole scores - ensure we have arrays
  const holeScores = score.hole_scores || [];
  
  // For 9-hole rounds, only use the 9 holes played
  const frontNine = holeScores.slice(0, 9);
  const backNine = isNineHoleRound ? [] : holeScores.slice(9, 18);
  
  // Calculate totals
  const frontTotal = frontNine.reduce((sum, h) => {
    const s = typeof h === 'object' ? h?.score : h;
    return sum + (parseInt(s) || 0);
  }, 0);
  const backTotal = isNineHoleRound ? 0 : backNine.reduce((sum, h) => {
    const s = typeof h === 'object' ? h?.score : h;
    return sum + (parseInt(s) || 0);
  }, 0);

  // Format date
  const scoreDate = score.date ? new Date(score.date).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit'
  }) : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });

  // Helper to get score value
  const getScoreValue = (h) => {
    if (h === null || h === undefined || h === '') return '-';
    if (typeof h === 'object') {
      // Handle object format: { hole, score, par }
      const score = h?.score;
      if (score === null || score === undefined || score === '') return '-';
      return parseInt(score) || '-';
    }
    // Handle primitive values
    const num = parseInt(h);
    return isNaN(num) ? '-' : num;
  };

  // Render score cell with simple styling (no par-based formatting)
  const renderScoreCell = (score, parHole, idx, prefix) => {
    const scoreVal = getScoreValue(score);
    
    return (
      <div key={`${prefix}-score-${idx}`} className="flex items-center justify-center py-1.5">
        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-[#3a4a3a]">
          {scoreVal}
        </span>
      </div>
    );
  };

  // Download scorecard as image
  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      const link = document.createElement('a');
      link.download = `par-seeker-scorecard-${scoreDate.replace(/\//g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Share to feed
  const handleShare = async () => {
    if (!cardRef.current || !onShareToFeed) return;
    setIsSharing(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      const imageData = canvas.toDataURL('image/png');
      await onShareToFeed(imageData, score);
    } catch (error) {
      console.error('Error sharing:', error);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/95 overflow-y-auto">
      {/* Header controls - fixed at top */}
      <div className="fixed top-0 left-0 right-0 flex justify-between items-center p-4 z-20 bg-gradient-to-b from-black/80 to-transparent">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center text-white hover:bg-white/40 transition-colors backdrop-blur-sm"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex gap-2">
          <button
            onClick={handleDownload}
            disabled={isGenerating}
            className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center text-white hover:bg-white/40 transition-colors backdrop-blur-sm"
          >
            <Download className="w-5 h-5" />
          </button>
          <button className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center text-white hover:bg-white/40 transition-colors backdrop-blur-sm">
            <MoreHorizontal className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Scrollable container */}
      <div className="w-full max-w-md mx-auto px-4 pt-16 pb-6">
        {/* The actual card that gets captured */}
        <div 
          ref={cardRef}
          className="relative overflow-hidden rounded-lg shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, #1a2e1a 0%, #152515 50%, #0d1a0d 100%)',
          }}
        >
          {/* Background Image - Eagle on Golf Course - lighter transparency */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: 'url(/images/eagle-golf-bg.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              opacity: 0.55
            }}
          />

          {/* Dark gradient overlay for text readability - lighter overlay */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, rgba(15,25,15,0.5) 0%, rgba(10,20,10,0.55) 40%, rgba(5,15,5,0.7) 100%)'
            }}
          />

          {/* Content */}
          <div className="relative z-10 p-5">
            {/* Developed by Par Seeker - Text only, no logo */}
            <div className="text-center mb-4">
              <p 
                className="text-xs tracking-[3px] font-bold uppercase"
                style={{ 
                  color: '#b0d8a0',
                  textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                }}
              >
                Developed by Par Seeker
              </p>
            </div>

            {/* Checkmark and user message */}
            <div className="flex items-start gap-2 mb-3">
              <div className="w-5 h-5 rounded-full bg-[#4a9a3a] flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
              <p 
                className="text-sm leading-tight font-semibold"
                style={{ 
                  color: '#ffffff',
                  textShadow: '0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.5)'
                }}
              >
                {user?.name || 'Golfer'} posted a score on the <span style={{ color: '#90d080' }}>PAR SEEKER</span> app at
              </p>
            </div>

            {/* Course Name - with strong shadow for visibility, no background box */}
            <h2 
              className="text-2xl font-black tracking-wide mb-5 uppercase leading-tight"
              style={{ 
                color: '#ffffff',
                textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 20px rgba(0,0,0,0.9), 0 4px 16px rgba(0,0,0,0.8)'
              }}
            >
              {course?.name || score.course_name || 'GOLF COURSE'}
            </h2>

            {/* Score Display - with proper spacing */}
            <div className="flex items-baseline gap-3 mb-3">
              <span 
                className="text-5xl font-black tracking-tight" 
                style={{ 
                  fontFamily: 'system-ui, -apple-system, sans-serif', 
                  lineHeight: 1,
                  color: '#ffffff',
                  textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 16px rgba(0,0,0,0.7)'
                }}
              >
                {totalScore}
              </span>
            </div>

            {/* Course Details - with more spacing from score */}
            <div 
              className="flex flex-wrap items-center gap-1.5 text-xs mb-5 font-semibold"
              style={{ 
                color: '#c0e8b0',
                textShadow: '0 1px 3px rgba(0,0,0,0.9)'
              }}
            >
              <span>{scoreDate}</span>
              <span style={{ color: '#90c080' }}>•</span>
              <span>Par {par}</span>
              <span style={{ color: '#90c080' }}>•</span>
              <span className="uppercase">{score.tees || 'WHITE'}</span>
            </div>

            {/* Scorecard Grid - sharper corners */}
            <div className="bg-white rounded overflow-hidden shadow-lg mb-4">
              {/* Front 9 */}
              <div className="grid grid-cols-10 text-center text-[10px]">
                {/* Hole numbers */}
                {[1,2,3,4,5,6,7,8,9,'Out'].map((h, i) => (
                  <div key={`front-hole-${i}`} className={`py-1.5 font-semibold text-gray-600 border-b border-gray-200 ${i === 9 ? 'bg-gray-100' : ''}`}>
                    {h}
                  </div>
                ))}
                
                {/* Scores */}
                {frontNine.length > 0 ? frontNine.map((h, i) => renderScoreCell(h, null, i, 'front')) : 
                  Array(9).fill(null).map((_, i) => (
                    <div key={`front-empty-${i}`} className="py-1.5 text-gray-400 text-xs">-</div>
                  ))
                }
                <div className="py-1.5 font-bold text-[#3a4a3a] bg-gray-100 text-xs">{frontTotal || '-'}</div>
              </div>

              {/* Back 9 - Only for 18-hole rounds */}
              {!isNineHoleRound && (
                <div className="grid grid-cols-10 text-center text-[10px] border-t border-gray-300">
                  {/* Hole numbers */}
                  {[10,11,12,13,14,15,16,17,18,'In'].map((h, i) => (
                    <div key={`back-hole-${i}`} className={`py-1.5 font-semibold text-gray-600 border-b border-gray-200 ${i === 9 ? 'bg-gray-100' : ''}`}>
                      {h}
                    </div>
                  ))}
                  
                  {/* Scores */}
                  {backNine.length > 0 ? backNine.map((h, i) => renderScoreCell(h, null, i, 'back')) : 
                    Array(9).fill(null).map((_, i) => (
                      <div key={`back-empty-${i}`} className="py-1.5 text-gray-400 text-xs">-</div>
                    ))
                  }
                  <div className="py-1.5 font-bold text-[#3a4a3a] bg-gray-100 text-xs">{backTotal || '-'}</div>
                </div>
              )}
            </div>

            {/* Bottom Section - Par Seeker branding (no logo for cleaner look) */}
            <div className="flex items-center justify-between">
              <div 
                className="text-[9px] leading-tight"
                style={{ 
                  color: '#d0ecc0',
                  textShadow: '0 1px 2px rgba(0,0,0,0.9)'
                }}
              >
                <p>Get a Handicap Index and track</p>
                <p>stats with the <span className="font-bold" style={{ color: '#ffffff' }}>PAR SEEKER</span> app</p>
              </div>
              
              {/* Handicap Display */}
              <div 
                className="rounded-lg px-3 py-1.5 text-center"
                style={{
                  backgroundColor: 'rgba(30,60,30,0.9)',
                  border: '1px solid rgba(80,140,80,0.6)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.5)'
                }}
              >
                <p 
                  className="text-[7px] tracking-wider font-bold uppercase"
                  style={{ color: '#a0d090' }}
                >
                  Handicap Index
                </p>
                <p 
                  className="text-sm font-black leading-none"
                  style={{ 
                    color: '#ffffff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  {currentHandicap !== null && currentHandicap !== undefined
                    ? parseFloat(currentHandicap).toFixed(1) 
                    : '--.-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons - Below the card with proper spacing */}
        <div className="flex gap-3 mt-5">
          <Button
            variant="outline"
            onClick={() => {
              onClose();
              // Call onEdit if provided to open the edit modal
              if (onEdit && score) {
                setTimeout(() => onEdit(score), 100);
              }
            }}
            className="flex-1 bg-transparent border-white/30 text-white hover:bg-white/10 h-12 text-base font-semibold rounded-xl"
          >
            Edit
          </Button>
          <Button
            onClick={handleShare}
            disabled={isSharing}
            className="flex-1 bg-[#4a9a3a] hover:bg-[#3a8a2a] text-white h-12 text-base font-semibold rounded-xl"
          >
            {isSharing ? 'Sharing...' : 'Share'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareableScorecard;
